import React from 'react';
import {isPlainObject, isObject, isEmpty} from 'lodash/lang';
import {capitalize} from 'lodash/string';
import orderBy from 'lodash/orderBy';
import reverse from 'lodash/reverse';
import XLSX from 'xlsx';
import {formValueSelector, reduxForm} from 'redux-form';
import moment from 'moment';
import {connect} from 'react-redux';

const objToQuery = (obj) =>
  Object.entries(obj)
    .filter(([k, v]) => typeof v !== 'undefined')
    .map(([k, v]) => `${k}=${v}`)
    .join('&');

const buildQueryString = (obj) => encodeURI(objToQuery(obj));

const normalizeUrl = (url) => (url.slice(-1) === '?' ? url.slice(0, url.length - 1) : url);

export const buildUrl = (url, query) =>
  normalizeUrl(`${process.env.SERVER_BASE_URL}/api${url}?${buildQueryString(query)}`);

export const buildWebsocketUrl = (url, query) =>
  normalizeUrl(`${process.env.WEBSOCKET_BASE_URL}${url}?${buildQueryString(query)}`);

export const toCamelCase = (v) => v.replace(/\B_[a-z0-9]/g, (m) => m[1].toUpperCase());

export const snakeToCamel = (data) => {
  if (Array.isArray(data)) {
    const ret = [];
    for (let item of data) {
      ret.push(snakeToCamel(item));
    }
    return ret;
  } else if (isPlainObject(data)) {
    let ret = {};
    for (let [k, v] of Object.entries(data)) {
      ret[toCamelCase(k)] = snakeToCamel(v);
    }
    return ret;
  }
  return data;
};

export const toSnakeCase = (v) => v.replace(/\B[a-z0-9][A-Z]/g, (m) => m[0] + '_' + m[1].toLowerCase());

export const camelToSnake = (data) => {
  if (Array.isArray(data)) {
    const ret = [];
    for (let item of data) {
      ret.push(camelToSnake(item));
    }
    return ret;
  } else if (isPlainObject(data)) {
    let ret = {};
    for (let [k, v] of Object.entries(data)) {
      ret[toSnakeCase(k)] = camelToSnake(v);
    }
    return ret;
  }
  return data;
};

const getFieldValues = (state, fields, selector) => {
  if (isEmpty(fields)) {
    return {};
  }
  const ret = {};
  for (let field of fields) {
    ret[field] = selector(state, field);
  }
  return ret;
};

export const reduxFormWrapper = (class_, formName) =>
  reduxForm({
    form: formName || class_.constructor.name,
    enableReinitialize: true,
  })(class_);

export const makeForm = (form, formName, mapStateToProps, mapDispatchToProps, selectFields) => {
  const selector = formValueSelector(formName);
  let newStateToProps;
  if (!mapStateToProps) {
    newStateToProps = (state) => getFieldValues(state, selectFields, selector);
  } else {
    newStateToProps = (state, ownProps) => ({
      ...mapStateToProps(state, ownProps),
      ...getFieldValues(state, selectFields, selector),
    });
  }
  return connect(newStateToProps, mapDispatchToProps)(reduxFormWrapper(form, formName));
};

export const excludeObjNullValue = (data) => {
  const ret = {};
  for (let [k, v] of Object.entries(data)) {
    if (v !== null && v !== undefined) {
      ret[k] = v;
    }
  }
  return ret;
};

export const parseIfIsNumber = (v, action = parseInt) => {
  if (Array.isArray(v) || isObject(v)) {
    return;
  }
  const parsed = action(v);
  return isNaN(parsed) ? undefined : parsed;
};

export const calcTableEntriesInfo = (itemsPerPage, currentPage, totalCount) =>
  `Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)} ` +
  `to ${Math.min(currentPage * itemsPerPage, totalCount)} ` +
  `of ${totalCount} ${totalCount === 1 ? 'entry' : 'entries'}`;

export const getOptions = (obj) => Object.entries(obj).map(([value, label]) => ({value, label}));

moment.locale('en');
const backendDatetimeFormat = moment.ISO_8601;
const datetimeFormat = 'DD/MM/YYYY h:mmA';
const dateFormat = 'DD/MM/YYYY';

export const momentDatetime = (datetime, isUtc = false, format = backendDatetimeFormat) => {
  let parsedDatetime;
  if (datetime === undefined || datetime === null) {
    parsedDatetime = moment();
  } else if (moment.isMoment(datetime)) {
    parsedDatetime = datetime;
  } else if (datetime instanceof Date) {
    parsedDatetime = moment(datetime);
  } else if (!isEmpty(datetime) && typeof datetime === 'string') {
    parsedDatetime = moment(datetime, format);
  } else if (typeof datetime === 'number') {
    parsedDatetime = moment.unix(datetime);
  } else {
    throw TypeError(`Invalid type of datetime: ${datetime} (type: ${typeof datetime})`);
  }
  return isUtc ? parsedDatetime.utc(parsedDatetime).local() : parsedDatetime.local();
};

export const formatDatetime = (datetime, isUtc = true, format = datetimeFormat) =>
  moment.isMoment(datetime) ||
  datetime instanceof Date ||
  (!isEmpty(datetime) && typeof datetime === 'string') ||
  typeof datetime === 'number'
    ? momentDatetime(datetime, isUtc).format(format)
    : '';

export const formatDate = (datetime, isUtc = true, format = dateFormat) => formatDatetime(datetime, isUtc, format);

export const dateToDatetimeString = (date, time = '00:00:00') => {
  const datetime = new Date(`${date}T${time}`).toISOString();
  return moment.utc(datetime).local();
};

export function debounce(fn, ms) {
  if (this.debounceTimeoutId) {
    clearTimeout(this.debounceTimeoutId);
  }
  this.debounceTimeoutId = setTimeout(fn, ms);
}

export const buildUserFullName = (user, withUsername = false, withEmail = false) => {
  if (!user) {
    return '';
  }
  const firstName = user.firstName || (user.get ? user.get('firstName', '') : '') || '';
  const lastName = user.lastName || (user.get ? user.get('lastName', '') : '') || '';
  const username = user.username || (user.get ? user.get('username', '') : '') || '';
  const email = user.email || (user.get ? user.get('email', '') : '') || '';
  if (!firstName && !lastName && !username && !email) {
    return '';
  }
  let fullName = `${capitalize(firstName)} ${capitalize(lastName)}`;
  if (withUsername) {
    fullName = `${fullName} (${username})`;
  }
  if (withEmail) {
    fullName = `${fullName} (${email})`;
  }
  return fullName;
};

export const formatRole = (role) => {
  switch (role) {
    case 'kluwell_admin':
      return 'kluwell admin';
    case 'school_coordinator':
      return 'school coordinator';
    case 'teacher':
      return 'teacher';
    case 'parent':
      return 'parent';
    case 'reader':
      return 'student';
    default:
      throw Error(`Invalid role: ${role}`);
  }
};

export const levelMap = {
  1: 'Yellow',
  2: 'Green',
  3: 'Orange',
};

export const levelOptions = [
  {value: 1, label: 'Yellow'},
  {value: 2, label: 'Green'},
  {value: 3, label: 'Orange'},
];

export const getAvailableRolesToCreate = (role) => {
  switch (role) {
    case 'kluwell_admin':
      return [
        {value: 'kluwell_admin', label: 'Kluwell Admin'},
        {value: 'school_coordinator', label: 'School Coordinator'},
        {value: 'teacher', label: 'Teacher'},
        // {value: 'parent', label: 'Parent'},
        // {value: 'reader', label: 'Student'},
      ];
    case 'school_coordinator':
      return [
        {value: 'school_coordinator', label: 'School Coordinator'},
        {value: 'teacher', label: 'Teacher'},
      ];
    default:
      return [];
  }
};

export const getAvailableRolesToInvite = (role) => {
  switch (role) {
    case 'kluwell_admin':
      return [
        {value: 'kluwell_admin', label: 'Kluwell Admin'},
        {value: 'school_coordinator', label: 'School Coordinator'},
        {value: 'teacher', label: 'Teacher'},
        // {value: 'parent', label: 'Parent'},
      ];
    case 'school_coordinator':
      return [
        {value: 'school_coordinator', label: 'School Coordinator'},
        {value: 'teacher', label: 'Teacher'},
        // {value: 'parent', label: 'Parent'},
      ];
    case 'teacher':
      return [
        // {value: 'parent', label: 'Parent'}
      ];
    default:
      return [];
  }
};

export const getViewableRolesToInvite = (role) => {
  switch (role) {
    case 'kluwell_admin':
      return [
        {value: 'kluwell_admin', label: 'Kluwell Admin'},
        {value: 'school_coordinator', label: 'School Coordinator'},
        {value: 'teacher', label: 'Teacher'},
        // {value: 'parent', label: 'Parent'},
        {value: 'reader', label: 'Student'},
      ];
    case 'school_coordinator':
      return [
        {value: 'school_coordinator', label: 'School Coordinator'},
        {value: 'teacher', label: 'Teacher'},
        // {value: 'parent', label: 'Parent'},
        {value: 'reader', label: 'Student'},
      ];
    case 'teacher':
      return [
        // {value: 'parent', label: 'Parent'},
        {value: 'reader', label: 'Student'},
      ];
    default:
      return [];
  }
};

export const getAvailableRolesOfMessageReceiver = (role) => {
  switch (role) {
    case 'kluwell_admin':
      return [
        {value: 'kluwell_admin', label: 'Kluwell Admin'},
        {value: 'school_coordinator', label: 'School Coordinator'},
        {value: 'teacher', label: 'Teacher'},
        // {value: 'parent', label: 'Parent'},
        {value: 'reader', label: 'Student'},
      ];
    case 'school_coordinator':
      return [
        {value: 'school_coordinator', label: 'School Coordinator'},
        {value: 'teacher', label: 'Teacher'},
        // {value: 'parent', label: 'Parent'},
        {value: 'reader', label: 'Student'},
      ];
    case 'teacher':
      return [
        {value: 'teacher', label: 'Teacher'},
        // {value: 'parent', label: 'Parent'},
        {value: 'reader', label: 'Student'},
      ];
    // case 'parent':
    //   return [{value: 'reader', label: 'Student'}];
    default:
      return [];
  }
};

export const getAvailableRolesOfMessageSender = (role) => {
  switch (role) {
    case 'kluwell_admin':
      return [{value: 'kluwell_admin', label: 'Kluwell Admin'}];
    case 'school_coordinator':
      return [
        {value: 'kluwell_admin', label: 'Kluwell Admin'},
        {value: 'school_coordinator', label: 'School Coordinator'},
      ];
    case 'teacher':
      return [
        {value: 'kluwell_admin', label: 'Kluwell Admin'},
        {value: 'school_coordinator', label: 'School Coordinator'},
        {value: 'teacher', label: 'Teacher'},
      ];
    // case 'parent':
    //   return [
    //     {value: 'kluwell_admin', label: 'Kluwell Admin'},
    //     {value: 'school_coordinator', label: 'School Coordinator'},
    //     {value: 'teacher', label: 'Teacher'},
    //   ];
    default:
      return [];
  }
};

export const getReadingReactions = (activeReadingReactions) => {
  return orderBy(activeReadingReactions, ['order'], ['desc']).map(({image, id}, idx) => ({
    id,
    value: idx * 2,
    image: <img src={buildMediaFileUrl({url: image})} />,
  }));
};

export const getReaderAvatarUrlMap = (level) => {
  level = levelMap[level].toLowerCase();
  const map = {};
  for (let i = 1; i <= 6; ++i) {
    const imageUrl = `/images/avatars/${level}/${i}.png`;
    map[imageUrl] = <img src={imageUrl} />;
  }
  return map;
};

export const getReaderAvatarOptions = (activeUserAvatars) =>
  orderBy(activeUserAvatars, ['order']).map(({image}) => ({
    value: image,
    image: <img src={buildMediaFileUrl({url: image})} />,
  }));

export const arrayToIdKeyedObject = (arr, keyName = 'id') => {
  if (!Array.isArray(arr)) {
    return arr;
  }
  const ret = {};
  if (keyName === null) {
    for (const [k, v] of Object.entries(arr)) {
      ret[k] = v;
    }
  } else {
    for (const v of arr) {
      ret[v[keyName]] = v;
    }
  }
  return ret;
};

export const countryMap = {
  AU: 'Australia',
  NZ: 'New Zealand',
};

export const stateMap = {
  AU: {
    NSW: 'New South Wales',
    QLD: 'Queensland',
    TAS: 'Tasmania',
    SA: 'South Australia',
    WA: 'Western Australia',
    VIC: 'Victoria',
    ACT: 'Australian Capital Territory',
  },
  NZ: {
    AUK: 'Auckland',
    BOP: 'Bay of Plenty',
    CAN: 'Canterbury',
    GIS: 'Gisborne',
    HKB: "Hawke's Bay",
    MBH: 'Marlborough',
    MWT: 'Manawatu-Wanganui',
    NSN: 'Nelson',
    NTL: 'Northland',
    OTA: 'Otago',
    STL: 'Southland',
    TAS: 'Tasman',
    TKI: 'Taranaki',
    WKO: 'Waikato',
    WGN: 'Wellington',
    WTC: 'West Coast',
    CIT: 'Chatham Islands Territory',
  },
};

export const getStateMap = (countryCode) => stateMap[countryCode];

export const getStateOptions = (countryCode) =>
  !countryCode ? [] : Object.entries(getStateMap(countryCode)).map(([value, label]) => ({value, label}));

export const countryOptions = Object.entries(countryMap).map(([value, label]) => ({value, label}));

export const normalizeAddress = (data) => {
  const addressLineOne = data.addressLine1 || '';
  const suburb = data.suburb || '';
  const state = (data.state && getStateMap(data.country)[data.state]) || '';
  const country = (data.country && countryMap[data.country]) || '';
  const formatAddressLineOne = addressLineOne && `${addressLineOne}, `;
  const formatSuburb = suburb && `${suburb}, `;
  const formatState = state && `${state}, `;
  return `${formatAddressLineOne}${formatSuburb}${formatState}${country}`;
};

export const formatTipType = (type) => {
  switch (type) {
    case 'book':
      return 'Book';
    default:
      return formatRole(type);
  }
};

export const readExcelFile = (file) => {
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      try {
        const workbook = XLSX.read(data, {type: 'array'});
        resolve(XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]));
      } catch (e) {
        reject('Whoops! We can not read the file you selected. Please select another one.');
      }
    };
    reader.readAsArrayBuffer(file);
  });
};

/**
 * https://stackoverflow.com/a/29304414
 */
export const generateCSVFile = (data, fileName, mimeType) => {
  let csvContent = '';
  data.forEach((infoArray, index) => {
    const dataString = infoArray.join(',');
    csvContent += index < data.length ? dataString + '\n' : dataString;
  });

  downloadBlob(csvContent, fileName, mimeType);
};

export const downloadBlob = (blob, fileName, mimeType = 'application/octet-stream') => {
  const a = document.createElement('a');
  if (window.URL && 'download' in a) {
    a.href = window.URL.createObjectURL(new Blob([blob], {type: mimeType}));
    a.setAttribute('download', fileName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } else {
    location.href = 'data:application/octet-stream,' + encodeURIComponent(blob);
  }
};

export const tipTypeMap = Object.freeze({
  kluwell_admin: 'Kluwell Admin',
  school_coordinator: 'School Coordinator',
  teacher: 'Teacher',
  parent: 'Parent',
  reader: 'Student',
  book: 'Book',
});

export const tipTypeOptions = Object.entries(tipTypeMap).map(([value, label]) => ({value, label}));

export const getDefaultSubscriptionExpiryDate = () =>
  moment()
    .set('month', 11)
    .set('date', 31)
    .endOf('day');

export const gameLevelMap = Object.freeze({
  1: 'monsters-and-cake',
  2: 'mountain-hop',
  3: 'cubeform',
});

export const requestNotificationPermission = () => {
  if ('Notification' in window && Notification.permission !== 'denied') {
    Notification.requestPermission();
  }
};

export const getColor = ({palette, lightModeColor, darkModeColor}) =>
  palette.type === 'dark' ? darkModeColor : lightModeColor;

export const createKeyboardEvent = (key, keyCode) => {
  return new KeyboardEvent('keydown', {
    key: key,
    code: key,
    location: 0,
    ctrlKey: false,
    shiftKey: false,
    altKey: false,
    metaKey: false,
    repeat: false,
    isComposing: false,
    charCode: 0,
    keyCode: keyCode,
    which: keyCode,
    bubbles: true,
    cancelable: true,
    composed: true,
    defaultPrevented: true,
    isTrusted: true,
    returnValue: false,
  });
};

export const getSeededRandomFunc = (seed = 42) => {
  let _seed = seed;
  return () => {
    _seed = (_seed * 16807) % 2147483647;
    return (_seed - 1) / 2147483646;
  };
};

export const getRandomInt = ({max, min, randomFunc}) => Math.floor(randomFunc() * (max - min + 1)) + min;

export const buildMediaFileUrl = ({url, placeholder}) =>
  !url ? placeholder : url.startsWith('http') ? url : `${process.env.SERVER_BASE_URL}${url}`;

export const isNullOrUndefined = (value) => value === undefined || value === null;

export const studentSetReadingPeriodsMap = Object.freeze({
  3: 'Within 3 days',
  7: 'Within 7 days',
});

export const studentSetReadingPeriodOptions = [
  ...getOptions(studentSetReadingPeriodsMap),
  {value: null, label: 'No time frame'},
];
